import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { X, Copy, Printer } from "lucide-react";

// Tarifs de location véhicule (montants fixes, non affichés à l'utilisateur)
const LOCATION_RATES = { 6: 260, 8: 310, 10: 340, 12: 380, 15: 400 };
const CHARGES_PATRONALES = 0.65;   // taux fixe, jamais affiché
const FRAIS_GENERAUX = 0.05;       // taux fixe, jamais affiché
const KM_RATE = 0.45;              // défrayements, carburant et autres frais

const DISCLAIMER =
  "Prix de cession hors V.H.R. (voyage, hébergement, restauration), hors droits et hors rémunérations producteur et diffuseur. Pour toute demande particulière, nous consulter.";

const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
const parseNum = (v) => {
  const n = parseFloat(String(v).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : 0;
};
const ceilEur = (n) => (n > 0 ? Math.ceil(n - 1e-9) : 0); // arrondi à l'euro supérieur (jamais de « -0 »)

const FIELD = "w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2.5 transition-colors placeholder:text-white/25";
const LABEL = "font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]";

export function CessionSimulator({ onClose, defaultName = "" }) {
  const [titre, setTitre] = useState("");
  const [clientNom, setClientNom] = useState(defaultName);
  const [destRaison, setDestRaison] = useState("");
  const [destAdresse, setDestAdresse] = useState("");
  const [destCpVille, setDestCpVille] = useState("");
  const [q1, setQ1] = useState("");  // comédiens au plateau
  const [m1, setM1] = useState("");  // rémunération brute moyenne artistes / représentation
  const [q2, setQ2] = useState("");  // techniciens et administratifs
  const [m2, setM2] = useState("");  // rémunération brute moyenne techno/admin / représentation
  const [m3, setM3] = useState("");  // provision transport (montant libre)
  const [q3, setQ3] = useState("");  // km aller/retour
  const [location, setLocation] = useState(false);
  const [cylindree, setCylindree] = useState(6);

  const brutRaw = parseNum(q1) * parseNum(m1) + parseNum(q2) * parseNum(m2);
  const transportRaw = parseNum(m3) + (location ? LOCATION_RATES[cylindree] : 0) + KM_RATE * parseNum(q3);
  // Tous les montants sont arrondis à l'euro supérieur ; les totaux sont calculés
  // à partir des lignes arrondies pour que l'addition affichée tombe toujours juste.
  const brut = ceilEur(brutRaw);
  const charges = ceilEur(brutRaw * CHARGES_PATRONALES);
  const plateau = brut + charges;
  const transport = ceilEur(transportRaw);
  const fraisGeneraux = ceilEur(FRAIS_GENERAUX * (plateau + transport));
  const total = plateau + transport + fraisGeneraux;
  const valid = brutRaw > 0 || transportRaw > 0;

  const destLines = [destRaison, destAdresse, destCpVille].map((s) => s.trim()).filter(Boolean);

  const summaryLines = () => [
    // En-tête : émetteur (le client connecté) en haut à gauche, puis le destinataire du devis
    clientNom.trim() || "—",
    ...(destLines.length ? ["", `Destinataire : ${destLines[0]}`, ...destLines.slice(1)] : []),
    "",
    "CALCUL D'UN PRIX DE CESSION (données non contractuelles)",
    ...(titre.trim() ? [`Spectacle : ${titre.trim()}`] : []),
    "Pour 1 date — hors V.H.R., hors droits, hors rémunérations producteur et diffuseur",
    "",
    "PLATEAU",
    `Total brut : ${eur(brut)}`,
    `Charges patronales : ${eur(charges)}`,
    `Sous-total plateau : ${eur(plateau)}`,
    "",
    "TRANSPORT",
    `Sous-total transport : ${eur(transport)}`,
    "",
    `Frais généraux et provisions charges : ${eur(fraisGeneraux)}`,
    `TOTAL GÉNÉRAL (prix de cession) : ${eur(total)}`,
    "",
    DISCLAIMER,
  ];

  const copyResult = async () => {
    try {
      await navigator.clipboard.writeText(summaryLines().join("\n"));
      toast.success("Estimation copiée dans le presse-papiers.");
    } catch {
      toast.error("Copie impossible dans le presse-papiers.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-[#0B2A52] border border-white/10 rounded-xl p-8 max-h-[90vh] overflow-y-auto"
        data-testid="cession-modal"
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-serif text-2xl">Prix de cession</h2>
          <button onClick={onClose} data-testid="cession-close-btn" aria-label="Fermer">
            <X className="w-5 h-5 text-[#A0B2CE]" />
          </button>
        </div>
        <p className="text-[11px] text-[#A0B2CE] mb-6">Pour 1 date — hors V.H.R., hors droits, hors rémunérations producteur et diffuseur.</p>

        <div className="space-y-6">
          <div>
            <label className={LABEL}>Titre du spectacle</label>
            <input type="text" value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Ex : Les Fourberies de Scapin"
              data-testid="cession-titre-input" className={FIELD} />
          </div>

          <div>
            <input type="text" value={clientNom} onChange={(e) => setClientNom(e.target.value)} placeholder="Ex : Le Collectif Birdland"
              aria-label="Nom du client (émetteur)"
              data-testid="cession-client-nom-input" className={FIELD} />
          </div>

          <fieldset className="space-y-4 border border-white/10 rounded-md p-4">
            <legend className="font-mono text-[11px] tracking-[0.15em] uppercase text-gold px-2">Destinataire du devis</legend>
            <div>
              <label className={LABEL}>Raison sociale</label>
              <input type="text" value={destRaison} onChange={(e) => setDestRaison(e.target.value)} placeholder="Ex : Théâtre de la Ville"
                data-testid="cession-dest-raison-input" className={FIELD} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>Adresse</label>
                <input type="text" value={destAdresse} onChange={(e) => setDestAdresse(e.target.value)} placeholder="Ex : 12 rue des Arts"
                  data-testid="cession-dest-adresse-input" className={FIELD} />
              </div>
              <div>
                <label className={LABEL}>CP Ville</label>
                <input type="text" value={destCpVille} onChange={(e) => setDestCpVille(e.target.value)} placeholder="Ex : 87000 Limoges"
                  data-testid="cession-dest-cpville-input" className={FIELD} />
              </div>
            </div>
          </fieldset>

          {/* PLATEAU */}
          <fieldset className="space-y-4 border border-white/10 rounded-md p-4">
            <legend className="font-mono text-[11px] tracking-[0.15em] uppercase text-gold px-2">Plateau</legend>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>Comédiens au plateau</label>
                <input type="number" min="0" value={q1} onChange={(e) => setQ1(e.target.value)} placeholder="Qté"
                  data-testid="cession-q1-input" className={FIELD} />
              </div>
              <div>
                <label className={LABEL}>Rémun. brute moyenne artistes (€ / représentation)</label>
                <input type="text" inputMode="decimal" value={m1} onChange={(e) => setM1(e.target.value)} placeholder="Ex : 150"
                  data-testid="cession-m1-input" className={FIELD} />
              </div>
              <div>
                <label className={LABEL}>Techniciens et administratifs</label>
                <input type="number" min="0" value={q2} onChange={(e) => setQ2(e.target.value)} placeholder="Qté"
                  data-testid="cession-q2-input" className={FIELD} />
              </div>
              <div>
                <label className={LABEL}>Rémun. brute moyenne techno/admin (€ / représentation)</label>
                <input type="text" inputMode="decimal" value={m2} onChange={(e) => setM2(e.target.value)} placeholder="Ex : 130"
                  data-testid="cession-m2-input" className={FIELD} />
              </div>
            </div>
          </fieldset>

          {/* TRANSPORT */}
          <fieldset className="space-y-4 border border-white/10 rounded-md p-4">
            <legend className="font-mono text-[11px] tracking-[0.15em] uppercase text-gold px-2">Transport (global)</legend>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>Provision transport (€, montant libre)</label>
                <input type="text" inputMode="decimal" value={m3} onChange={(e) => setM3(e.target.value)} placeholder="0"
                  data-testid="cession-m3-input" className={FIELD} />
              </div>
              <div>
                <label className={LABEL}>Nombre de km aller/retour</label>
                <input type="number" min="0" value={q3} onChange={(e) => setQ3(e.target.value)} placeholder="0"
                  data-testid="cession-q3-input" className={FIELD} />
              </div>
            </div>
            <div>
              <p className={`${LABEL} mb-2`}>Location véhicule</p>
              <div className="flex gap-2">
                {[{ v: true, l: "Oui" }, { v: false, l: "Non" }].map((o) => (
                  <button key={o.l} type="button" onClick={() => setLocation(o.v)} data-testid={`cession-location-${o.l.toLowerCase()}`}
                    className={`px-5 py-2 rounded-full border font-mono text-[11px] uppercase tracking-[0.1em] transition-colors ${
                      location === o.v ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-[#A0B2CE] hover:border-white/30"
                    }`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            {location && (
              <div>
                <p className={`${LABEL} mb-2`}>Cylindrée du véhicule</p>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(LOCATION_RATES).map((m3s) => (
                    <button key={m3s} type="button" onClick={() => setCylindree(Number(m3s))} data-testid={`cession-cyl-${m3s}`}
                      className={`px-4 py-2 rounded-full border font-mono text-[11px] transition-colors ${
                        cylindree === Number(m3s) ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-[#A0B2CE] hover:border-white/30"
                      }`}>
                      {m3s} m³
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-[#A0B2CE]/80 mt-2 italic">Au-delà de 15 m³ : sur devis.</p>
              </div>
            )}
          </fieldset>

          {valid && (
            <div className="border border-gold/30 rounded-md p-5 space-y-3" data-testid="cession-result">
              {titre.trim() && <p className="font-serif text-lg text-gold" data-testid="cession-titre-result">{titre.trim()}</p>}
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#A0B2CE]">Plateau</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#A0B2CE]">Total brut</span>
                <span className="font-mono" data-testid="cession-brut-value">{eur(brut)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#A0B2CE]">Charges patronales</span>
                <span className="font-mono" data-testid="cession-charges-value">{eur(charges)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#A0B2CE]">Sous-total plateau</span>
                <span className="font-mono" data-testid="cession-plateau-value">{eur(plateau)}</span>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#A0B2CE] pt-2">Transport</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#A0B2CE]">Sous-total transport</span>
                <span className="font-mono" data-testid="cession-transport-value">{eur(transport)}</span>
              </div>
              <div className="flex items-center justify-between text-sm pt-2">
                <span className="text-[#A0B2CE]">Frais généraux et provisions charges</span>
                <span className="font-mono" data-testid="cession-frais-value">{eur(fraisGeneraux)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-gold/30 pt-3">
                <span className="text-gold">Prix de cession (total général)</span>
                <span className="font-mono text-lg text-gold" data-testid="cession-total-value">{eur(total)}</span>
              </div>
            </div>
          )}

          {valid && (
            <div className="flex gap-3">
              <button onClick={copyResult} data-testid="cession-copy-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-gold hover:text-ink transition-colors">
                <Copy className="w-4 h-4" /> Copier
              </button>
              <button onClick={() => window.print()} data-testid="cession-print-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-gold hover:text-ink transition-colors">
                <Printer className="w-4 h-4" /> Imprimer
              </button>
            </div>
          )}

          <p className="text-[11px] text-[#A0B2CE] leading-relaxed italic" data-testid="cession-disclaimer">{DISCLAIMER}</p>
        </div>
      </motion.div>

      {/* Version imprimable (visible uniquement à l'impression) */}
      {valid && (
        <div className="estimator-print" aria-hidden="true">
          {summaryLines().join("\n")}
        </div>
      )}
    </div>
  );
}
